import { z } from 'zod';
import type { IsoDate } from './dates';
import { CONTRACT_KINDS, CONTRACT_STATUSES, RATE_UNITS, type ContractKind } from './enums';
import {
  expectedVersionSchema,
  idSchema,
  isoDateSchema,
  listParamsSchema,
  optionalDate,
  optionalText,
  requiredText,
} from './schemas';

// ───────────── РНОКПП та IBAN ─────────────

const TAX_ID_WEIGHTS = [-1, 5, 7, 9, 4, 6, 10, 5, 7];

/** РНОКПП (ІПН): 10 цифр, остання — контрольна. */
export function isValidTaxId(value: string): boolean {
  if (!/^\d{10}$/.test(value)) return false;
  const d = value.split('').map(Number);
  const sum = TAX_ID_WEIGHTS.reduce((s, w, i) => s + w * d[i]!, 0);
  return (((sum % 11) + 11) % 11) % 10 === d[9];
}

/** Дата народження, закодована в перших 5 цифрах РНОКПП (днів від 31.12.1899). */
export function taxIdBirthDate(value: string): IsoDate | null {
  if (!/^\d{10}$/.test(value)) return null;
  const days = Number(value.slice(0, 5));
  const date = new Date(Date.UTC(1899, 11, 31) + days * 86_400_000);
  return date.toISOString().slice(0, 10);
}

/** Український IBAN: UA + 2 контрольні цифри + 25 цифр, перевірка mod 97. */
export function isValidIban(value: string): boolean {
  const iban = value.replace(/\s+/g, '').toUpperCase();
  if (!/^UA\d{27}$/.test(iban)) return false;
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numeric = rearranged.replace(/[A-Z]/g, (c) => String(c.charCodeAt(0) - 55));
  let remainder = 0;
  for (const ch of numeric) remainder = (remainder * 10 + Number(ch)) % 97;
  return remainder === 1;
}

export function normalizeIban(value: string): string {
  return value.replace(/\s+/g, '').toUpperCase();
}

// ───────────── Договори ─────────────

export const CONTRACT_PREFIX: Record<ContractKind, string> = {
  employment: 'ТД',
  civil: 'ЦПД',
  fop: 'ФОП',
};

/** Номер договору: «ТД-2026/007» — префікс типу, рік, порядковий номер за рік. */
export function formatContractNumber(kind: ContractKind, year: number, seq: number): string {
  return `${CONTRACT_PREFIX[kind]}-${year}/${String(seq).padStart(3, '0')}`;
}

/** Порядковий номер з номера договору (для пошуку максимального за рік). */
export function parseContractSeq(number: string, kind: ContractKind, year: number): number | null {
  const m = new RegExp(`^${CONTRACT_PREFIX[kind]}-${year}/(\\d+)$`).exec(number);
  return m ? Number(m[1]) : null;
}

// ───────────── Особова справа і онбординг ─────────────

export interface DocumentTypeRule {
  id: string;
  name: string;
  isRequired: boolean;
  /** Порожній список — для всіх типів контрактів. */
  contractKinds: ContractKind[];
}

/**
 * Комплектність особової справи: які обов'язкові документи бракують з урахуванням
 * типів контрактів працівника.
 */
export function missingDocuments(
  types: DocumentTypeRule[],
  contractKinds: ContractKind[],
  uploadedTypeIds: string[],
): DocumentTypeRule[] {
  const have = new Set(uploadedTypeIds);
  return types.filter(
    (t) =>
      t.isRequired &&
      !have.has(t.id) &&
      (t.contractKinds.length === 0 || t.contractKinds.some((k) => contractKinds.includes(k))),
  );
}

/** Типовий чекліст онбордингу нового працівника. */
export const DEFAULT_ONBOARDING = [
  'Підписати договір і ознайомити з посадовими обов’язками',
  'Зібрати документи особової справи',
  'Створити обліковий запис у системі та корпоративну пошту',
  'Інструктаж з охорони праці та безпеки',
  'Ознайомити з політиками організації (захист даних, PSEA, кодекс поведінки)',
  'Видати обладнання та посвідчення',
  'Познайомити з керівником центру і командою',
];

// ───────────── Схеми ─────────────

const optionalId = idSchema.nullish().transform((v) => v ?? null);
const money = z.number().int().min(0).max(1e13);
const fte = z
  .number()
  .min(0.01, 'Мінімум 0,01')
  .max(1.5, 'Забагато')
  .transform((v) => Math.round(v * 100) / 100);

const taxId = z
  .string()
  .trim()
  .refine((v) => v === '' || isValidTaxId(v), 'Некоректний РНОКПП (контрольна цифра)')
  .nullish()
  .transform((v) => (v ? v : null));
const iban = z
  .string()
  .trim()
  .transform(normalizeIban)
  .refine((v) => v === '' || isValidIban(v), 'Некоректний IBAN')
  .nullish()
  .transform((v) => (v ? v : null));

const employeeFieldsBase = z.object({
  lastName: requiredText(100),
  firstName: requiredText(100),
  middleName: optionalText(100),
  phone: optionalText(100),
  email: z
    .email('Некоректний email')
    .max(200)
    .or(z.literal(''))
    .nullish()
    .transform((v) => (v ? v : null)),
  taxId,
  birthDate: optionalDate,
  address: optionalText(500),
  iban,
  bankName: optionalText(200),
  notes: optionalText(5000),
});
export const employeeFieldsSchema = employeeFieldsBase;
export type EmployeeFieldsInput = z.input<typeof employeeFieldsSchema>;
export const employeeUpdateSchema = employeeFieldsBase
  .partial()
  .extend({ id: idSchema })
  .extend(expectedVersionSchema.shape);

export const employeeListSchema = listParamsSchema.extend({
  status: z.enum(['active', 'dismissed']).optional(),
  projectId: idSchema.optional(),
  centerId: idSchema.optional(),
  contractKind: z.enum(CONTRACT_KINDS).optional(),
});

/** Оформлення переможця конкурсу працівником: працівник + призначення + чернетка договору. */
export const hireFromApplicationSchema = z.object({
  applicationId: idSchema,
  staffPositionId: optionalId,
  startsAt: isoDateSchema,
  endsAt: optionalDate,
  fte: fte.optional(),
  createContract: z.boolean().default(true),
});

export const dismissSchema = z.object({
  id: idSchema,
  date: isoDateSchema,
  reason: optionalText(1000),
  /** Корпоративна скринька: keep — лишити, block — змінити пароль, delete — видалити. */
  mailbox: z.enum(['keep', 'block', 'delete']).default('block'),
});

export const assignmentCreateSchema = z
  .object({
    employeeId: idSchema,
    staffPositionId: idSchema,
    contractId: optionalId,
    fte,
    startsAt: isoDateSchema,
    endsAt: optionalDate,
    notes: optionalText(1000),
  })
  .refine((v) => !v.endsAt || v.endsAt >= v.startsAt, {
    message: 'Дата завершення раніше дати початку',
    path: ['endsAt'],
  });
export const assignmentUpdateSchema = z.object({
  id: idSchema,
  contractId: optionalId.optional(),
  fte: fte.optional(),
  startsAt: isoDateSchema.optional(),
  endsAt: optionalDate.optional(),
  notes: optionalText(1000).optional(),
});

const contractFieldsBase = z.object({
  employeeId: idSchema,
  kind: z.enum(CONTRACT_KINDS),
  projectId: idSchema,
  staffPositionId: optionalId,
  signedAt: optionalDate,
  startsAt: isoDateSchema,
  endsAt: optionalDate,
  rate: money,
  rateUnit: z.enum(RATE_UNITS),
  totalCap: money.nullish().transform((v) => v ?? null),
  status: z.enum(CONTRACT_STATUSES),
  terms: optionalText(10_000),
});
export const contractCreateSchema = contractFieldsBase
  .extend({ status: z.enum(CONTRACT_STATUSES).default('draft') })
  .refine((v) => !v.endsAt || v.endsAt >= v.startsAt, {
    message: 'Дата завершення раніше дати початку',
    path: ['endsAt'],
  });
export type ContractCreateInput = z.input<typeof contractCreateSchema>;
export const contractUpdateSchema = contractFieldsBase
  .omit({ employeeId: true })
  .partial()
  .extend({ id: idSchema })
  .extend(expectedVersionSchema.shape);

export const contractListSchema = listParamsSchema.extend({
  status: z.enum(CONTRACT_STATUSES).optional(),
  kind: z.enum(CONTRACT_KINDS).optional(),
  projectId: idSchema.optional(),
  /** Договори, що закінчуються протягом N днів. */
  expiringDays: z.number().int().min(1).max(365).optional(),
});

export const amendmentCreateSchema = z.object({
  contractId: idSchema,
  date: isoDateSchema,
  description: requiredText(5000),
  newRate: money.nullish().transform((v) => v ?? null),
  newEndsAt: optionalDate,
});

export const documentTypeCreateSchema = z.object({
  name: requiredText(200),
  isRequired: z.boolean().default(true),
  contractKinds: z.array(z.enum(CONTRACT_KINDS)).default([]),
});
export const documentTypeUpdateSchema = z.object({
  id: idSchema,
  name: requiredText(200).optional(),
  isRequired: z.boolean().optional(),
  contractKinds: z.array(z.enum(CONTRACT_KINDS)).optional(),
});

export const onboardingTaskSchema = z.object({ employeeId: idSchema, title: requiredText(300) });

export const employeeAccountSchema = z.object({
  employeeId: idSchema,
  email: z.email('Некоректний email').max(200),
});
